import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from '../contexts/AuthContext';
import { AuthErrorBoundary } from '../components/UserTypeErrorBoundaries';
import { AuthenticationLoading } from '../components/LoadingStates';
import ErrorDisplay, { NetworkStatus } from '../components/ErrorDisplay';
import { useValidationErrors } from '../hooks/useErrorHandling';
import { classifyError } from '../utils/errorHandling';
import Logo from '../components/Logo';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const {
    login,
    logout,
    error: authError,
    isLoggingIn,
    loginAttempts,
    clearError,
    loading: authLoading,
    isAuthenticated,
    userRole
  } = useAuth();

  // Local error state to ensure error persists even if context re-renders
  const [localError, setLocalError] = useState<typeof authError>(null);

  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Use validation errors hook
  const {
    validationErrors,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    hasErrors,
  } = useValidationErrors();

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show session expired message from redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason === 'idle') {
      setLocalError(classifyError(new Error('Session expired due to 30 minutes of inactivity. Please log in again.')));
    } else if (reason === 'revoked') {
      setLocalError(classifyError(new Error('Your session has been revoked. Please log in again.')));
    }
  }, []);

  // Show loading during initial auth check or while role stabilizes post-login
  if (authLoading || (isAuthenticated && !userRole)) {
    return <AuthenticationLoading message="Checking authentication status..." />;
  }

  // If already authenticated with resolved role, show switch-account panel instead of redirecting
  if (isAuthenticated && userRole) {
    return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border border-navy-700 bg-navy-800 rounded-2xl">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-center text-white">
                You are already signed in
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center text-navy-200">
                You’re currently logged in. You can continue to your dashboard or log out to switch accounts.
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => navigate('/dashboard', { replace: true })}>
                  Continue to Dashboard
                </Button>
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => {
                    // Clean logout via AuthContext, with localStorage fallback
                    try {
                      logout();
                      navigate('/login', { replace: true });
                    } catch {
                      localStorage.removeItem('isAuthenticated');
                      localStorage.removeItem('user');
                      localStorage.removeItem('userRole');
                      localStorage.removeItem('authToken');
                      navigate('/login', { replace: true });
                      window.location.reload();
                    }
                  }}
                >
                  Log out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Form validation
  const validateForm = () => {
    clearAllErrors();
    let isValid = true;

    if (!credentials.email) {
      setFieldError('email', 'Email is required');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      setFieldError('email', 'Please enter a valid email address');
      isValid = false;
    }

    if (!credentials.password) {
      setFieldError('password', 'Password is required');
      isValid = false;
    }
    // No minimum length on login: the server is the source of truth
    // (backend /auth/login only requires non-empty; seeded passwords such as
    // `admin` are shorter than creation-time minimums and must still log in).

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check network connectivity
    if (!isOnline) {
      setFieldError('email', 'No internet connection. Please check your network and try again.');
      return;
    }

    // Single inline only: clear previous login error before attempt
    setLocalError(null);
    clearError();

    try {
      const result = await login(credentials.email, credentials.password);
      if (result.success) {
        // Dismiss on next successful: clear inline error and attempts banner handled by reset in context
        setLocalError(null);
        clearAllErrors();
        navigate('/dashboard');
      } else if (result.error) {
        // Login failed - use error returned from login (single inline)
        setLocalError(result.error);
      }
    } catch (err) {
      // Login threw an error - classify it and set local error
      const appError = classifyError(err);
      setLocalError(appError);
    }
  };

  const handleRetryLogin = async () => {
    setLocalError(null);
    clearError();
    clearAllErrors();
    if (!credentials.email || !credentials.password) return;
    try {
      const result = await login(credentials.email, credentials.password);
      if (result.success) {
        setLocalError(null);
        clearAllErrors();
        navigate('/dashboard');
      } else if (result.error) {
        setLocalError(result.error);
      }
    } catch (err) {
      setLocalError(classifyError(err));
    }
  };

  return (
    <AuthErrorBoundary>
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">

          {/* Network status indicator - single inline only: hide when inline login error is shown */}
          {!localError && <NetworkStatus isOnline={isOnline} className="fixed top-4 left-4 right-4 z-20" />}

          <div className="w-full max-w-md">
            {/* Login Card */}
            <Card className="shadow-xl border border-navy-700 bg-navy-800 rounded-2xl">
              <CardHeader className="space-y-6 pb-8">
                 {/* Branding */}
                 <div className="text-center">
                   <div className="flex items-center justify-center mb-2">
                     <Logo size="sm" />
                   </div>
                 </div>
                <CardTitle className="text-xl font-semibold text-center text-white">
                  Welcome Back
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Authentication Error Display - single inline only */}
                  {localError && (
                    <ErrorDisplay
                      error={localError}
                      onRetry={localError?.retryable && localError?.type !== 'auth' && localError?.type !== 'validation' && localError?.type !== 'permission' ? handleRetryLogin : undefined}
                      onDismiss={() => { setLocalError(null); clearError(); }}
                      variant="inline"
                    />
                  )}

                  {/* Login Attempts Warning */}
                  {loginAttempts > 2 && (
                    <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-3">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-sm text-yellow-300">
                          Multiple login attempts detected. Please verify your credentials.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-navy-100">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={credentials.email}
                      onChange={(e) => {
                        setCredentials({ ...credentials, email: e.target.value });
                        // Clear validation error when user starts typing
                        if (validationErrors.email) {
                          clearFieldError('email');
                        }
                        // Clear auth error when user modifies input (single inline only)
                        if (authError) {
                          clearError();
                        }
                        if (localError) {
                          setLocalError(null);
                        }
                      }}
                      className={`h-12 rounded-lg ${validationErrors.email
                        ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500'
                        : 'border-navy-700 focus:border-gold-500 focus:ring-gold-500'
                        }`}
                      aria-invalid={!!validationErrors.email}
                      aria-describedby={validationErrors.email ? 'email-error' : undefined}
                      disabled={isLoggingIn}
                    />
                    {validationErrors.email && (
                      <p id="email-error" className="text-sm text-red-400 mt-1">
                        {validationErrors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-navy-100">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={credentials.password}
                      onChange={(e) => {
                        setCredentials({ ...credentials, password: e.target.value });
                        // Clear validation error when user starts typing
                        if (validationErrors.password) {
                          clearFieldError('password');
                        }
                        // Clear auth error when user modifies input (single inline only)
                        if (authError) {
                          clearError();
                        }
                        if (localError) {
                          setLocalError(null);
                        }
                      }}
                      className={`h-12 rounded-lg ${validationErrors.password
                        ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500'
                        : 'border-navy-700 focus:border-gold-500 focus:ring-gold-500'
                        }`}
                      aria-invalid={!!validationErrors.password}
                      aria-describedby={validationErrors.password ? 'password-error' : undefined}
                      disabled={isLoggingIn}
                    />
                    {validationErrors.password && (
                      <p id="password-error" className="text-sm text-red-400 mt-1">
                        {validationErrors.password}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-medium bg-gold-500 hover:bg-gold-600 text-navy-950 focus:ring-gold-500 rounded-lg transition-colors duration-200"
                    disabled={isLoggingIn || !isOnline || hasErrors}
                  >
                    {isLoggingIn ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-navy-950" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {loginAttempts > 0 ? `Signing in... (Attempt ${loginAttempts + 1})` : 'Signing in...'}
                      </div>
                    ) : !isOnline ? (
                      'No Internet Connection'
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                </form>

                {/* Back to Home Button */}
                <div className="text-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="w-full h-12 text-base font-medium border-navy-700 text-navy-100 hover:bg-navy-900 rounded-lg"
                  >
                    Back to Home
                  </Button>
                </div>

                {/* Demo credentials removed */}
              </CardContent>
            </Card>
          </div>
        </div>
    </AuthErrorBoundary>
  );
};

export default Login;
